import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useForm } from 'antd/lib/form/Form';
import {
  Col,
  DatePicker,
  Form,
  Input,
  notification,
  Row,
  Tabs,
  Tag,
  Image,
  Button,
} from 'antd';
import DF from 'DisplayFramework';
import GetLabel from 'DisplayFramework/components/GetLabel';
import {
  SwapOutlined,
  AppstoreAddOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import {
  dateToString,
  shouldUpdateCompare,
  toDatePickerDate,
} from 'utils/utils2';
import {
  AddParMarketing,
  AssetUseCriteria,
  FilteringCriteria,
  getParMarketingDetailByMcid,
  ParMarketingLabelResponse,
  ParMarketingResponse,
  prFilteringCriteria,
  UpdateParMarketing,
  UpdateParMarketingStatus,
} from 'services/assetconfig';

import Value from 'components/Value';
import FormInput from 'components/FormInput';
import ProductInput from 'pages/ProposalV3/PropDetail/ProductInput';
import UtinaCard from 'components/UtinaCard';
import MarketingLabelList from './MarketingLabelList';
import ButtonOperation from 'components/ButtonOperation';
import ParListSelect from 'components/ParListSelect';
import MGListSelect from '../Components/MGListSelect';
import ISave from 'icons/ISave';
import MarketingPicturesModal from './MarketingPicturesModal';
import ModalController from 'components/ModalController';
import { CATALOG_IMG_URL } from 'utils/config';
import OfferInput from '../Management/ParametersFiltering/OfferInput';
import ParMarketingAsset from './ParMarketingAsset';
import PricingInput from 'pages/ProposalV3/PropDetail/PricingInput';
import getEnvValue from 'utils/getEnvValue';
import { Text } from '@react-pdf/renderer';
import { toCurrency } from 'utils/utils';
import ThirdPartyInput from 'components/ThirdPartyInput';
import { AssetRVCriteria } from 'services/pricing';

const { TabPane } = Tabs;

export type MarketingCompaignDetailProps = {
  mcid?: number;
  iscreate?: boolean;
  visible?: boolean;
  mc?: ParMarketingResponse;
};

const MarketingCompaignDetail = ({
  mcid,
  mc,
}: MarketingCompaignDetailProps) => {
  const [form] = useForm();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [activeKey, setActiveKey] = useState('1');
  const { idOrCreate } = useParams<{
    idOrCreate: string;
  }>();
  const [isCreate, setIsCreate] = useState(idOrCreate === 'create');
  const id = mcid ?? idOrCreate ?? undefined;

  const [catalog, setCatalog] = useState<any>();

  const {
    data: marketingComp,
    refetch,
    isFetching,
  } = useQuery(
    ['getParMarketingDetailByMcid', { mcid: id }],
    () =>
      getParMarketingDetailByMcid({ mcid: Number(id) }).then((res) => res.data),
    {
      enabled: !!id && id !== 'create',
    }
  );

  useEffect(() => {
    if (marketingComp) {
      form.setFieldsValue({
        ...marketingComp,
        startdate: toDatePickerDate(marketingComp.startdate),
        enddate: toDatePickerDate(marketingComp.enddate),
        picturebuttomlefturl: marketingComp.picturebuttomlefturl,
        picturebuttomrighturl: marketingComp.picturebuttomrighturl,
        picturecenterurl: marketingComp.picturecenterurl,
        picturemobileurl: marketingComp.picturemobileurl,
        picturetoplefturl: marketingComp.picturetoplefturl,
        picturetoprighturl: marketingComp.picturetoprighturl,
        pictureurl: marketingComp.pictureurl,
        ofrtype: marketingComp.ofrtype,
        manufacturer: {
          tpid: marketingComp.tpidmanufacturer,
          tpreference: marketingComp.manufacturerreference,
          tpname: marketingComp.manufacturername,
        },
        broker: {
          tpid: marketingComp.tpidbroker,
          tpname: marketingComp.brokername,
          tpreference: marketingComp.brokerreference,
        },
      });
      setCatalog({ pictureurl: marketingComp?.pictureurl });
    }
  }, [form, marketingComp]);

  const submit = (values: any) => {
    const startdate = form.getFieldValue('startdate');
    const enddate = form.getFieldValue('enddate');
    const data = {
      ...INITIAL_VALUES,
      ...marketingComp,
      ...values,
      picturebuttomlefturl: form.getFieldValue('picturebuttomlefturl'),
      picturebuttomrighturl: form.getFieldValue('picturebuttomrighturl'),
      picturecenterurl: form.getFieldValue('picturecenterurl'),
      picturemobileurl: form.getFieldValue('picturemobileurl'),
      picturetoplefturl: form.getFieldValue('picturetoplefturl'),
      picturetoprighturl: form.getFieldValue('picturetoprighturl'),
      pictureurl: form.getFieldValue('pictureurl'),
      startdate: dateToString(startdate),
      enddate: enddate ? dateToString(enddate) : '',
      ofrtype: form?.getFieldValue('ofrtype'),
      tpidmanufacturer: values.manufacturer?.tpid,
      manufacturername: values.manufacturer?.tpname,
      manufacturerreference: values.manufacturer?.tpreference,
      tpidbroker: values.broker?.tpid,
      brokername: values.broker?.tpname,
      brokerreference: values.broker?.tpreference,
    };
    if (isCreate) {
      return AddParMarketing({ request: data }).then((response) => {
        if (response.code === '200') {
          setIsCreate(false);
          notification.success({
            description: response.description,
            message: response.message,
          });
          navigate(
            `/configuration/marketingCompaign/${response.data.mcid}`
          );
        } else if (response.code === '404') {
          notification.error({ message: response.message });
        }
      });
    } else {
      return UpdateParMarketing({
        mcid: values.mcid,
        request: data,
      }).then((response) => {
        if (response.code === '200') {
          refetch();
          notification.success({
            description: response.description,
            message: response.message,
          });
        }
      });
    }
  };
  const getFilterList = (): prFilteringCriteria => {
    let assetList = form?.getFieldValue('assets');

    let assetUseCriteriaList: Array<AssetUseCriteria> = [];
    let offerCriteriaList: Array<FilteringCriteria> = [];
    let assetRVCriteriaList: Array<AssetRVCriteria> = [];

    let ret: prFilteringCriteria;
    offerCriteriaList.push({
      critcode: 'OFFER',
      critvalue: form?.getFieldValue('offerid') ?? '',
    });
    offerCriteriaList.push({
      critcode: 'PRCODE',
      critvalue: form?.getFieldValue('prcode') ?? '',
    });
    offerCriteriaList.push({
      critcode: 'TPTYPE',
      critvalue: form.getFieldValue('client')?.tptype ?? '',
    });
    offerCriteriaList.push({
      critcode: 'NETWORK',
      critvalue: form.getFieldValue('ctnetworkcode') ?? '',
    });
    offerCriteriaList.push({
      critcode: 'CLIRATING',
      critvalue: form.getFieldValue('client')?.tpsalestatus ?? '',
    });
    assetList?.forEach((a: any) => {
      offerCriteriaList.push({
        critcode: 'ASSETFAM',
        critvalue: a.assetfamily,
      });
      offerCriteriaList.push({ critcode: 'ASSETTYPE', critvalue: a.assettype });
      offerCriteriaList.push({
        critcode: 'ASSETCAT',
        critvalue: a.assetcategory,
      });

      offerCriteriaList.push({ critcode: 'POWER', critvalue: a.power ?? '' });
      // additional attributes :
      a.entityAddAttributList?.forEach((e: any) => {
        offerCriteriaList.push({
          critcode: e.attcode,
          critvalue: e.attstringvalue ?? e.attnumericvalue,
        });
      });

      if (getEnvValue('FLAG_APPLY_ASSET_USE')) {
        a.assetusereqList?.forEach((u: any) => {
          assetUseCriteriaList.push({
            assetfamily: a.assetfamily,
            assettype: a.assettype,
            assetcategory: a.assetcategory,
            catalogid: a.catalogid ?? '',
            attcode: u.attcode ?? '',
            attvalue: u.usevalue ?? '',
          });
        });
      }
    });

    ret = {
      prcode: form?.getFieldValue('flagmasterfacility')
        ? form?.getFieldValue('mfprcode')
        : form?.getFieldValue('prcode') ?? '',
      tpid: form.getFieldValue('client')?.tpid ?? 0,
      tpidbroker: form.getFieldValue('broker')?.tpid ?? 0,
      tpidmanufacturer: assetList ? assetList[0]?.tpidmanufacturer ?? 0 : 0,
      assetUseCriteria: assetUseCriteriaList,
      assetRVCriteria: assetRVCriteriaList,
      offerCriteria: offerCriteriaList,
      flagApplyAssetUse: getEnvValue('FLAG_APPLY_ASSET_USE'),
      flagApplyAttributesExtendDurationValues: true,
      flagApplyAttributesExtendFirstRentalValues: true,
      flagApplyAttributesExtendRVValues: true,
      flagApplyAttributesExtendRateValues: true,
      flagApplyExtendDurationValues: true,
      flagApplyExtendFirstRentalValues: true,
      flagApplyExtendRVValues: true,
      flagApplyExtendRateValues: true,
    };
    return ret;
  };
  return (
    <DF.Provider pagcode="marketingcompaigndetail">
      <Form
        form={form}
        initialValues={INITIAL_VALUES}
        labelAlign="left"
        onFinish={submit}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <div className="card-container">
          <Tabs
            onChange={(activeKey) => setActiveKey(activeKey)}
            defaultActiveKey="1"
            type="card"
            tabBarExtraContent={
              <Row>
                <div>
                  <Form.Item
                    noStyle
                    shouldUpdate={shouldUpdateCompare('mcstatus')}
                  >
                    {({ getFieldValue }) => {
                      let comcode = '';
                      let mcstatus = '';
                      switch (getFieldValue('mcstatus')) {
                        case 'INIT':
                          comcode = 'Activate';
                          mcstatus = 'ACTIVE';
                          break;
                        case 'ACTIVE':
                          comcode = 'Disabled';
                          mcstatus = 'DISABLED';
                          break;
                        case 'DISABLED':
                          comcode = 'Terminate';
                          mcstatus = 'END';
                          break;
                        case 'END':
                          comcode = 'Activate';
                          mcstatus = 'ACTIVE';
                          break;
                      }

                      return (
                        <ButtonOperation
                          size="large"
                          comcode={comcode}
                          icon={<SwapOutlined />}
                          onClick={() => {
                            UpdateParMarketingStatus({
                              mcid: form.getFieldValue('mcid'),
                              mcstatus: mcstatus,
                            }).then((response) => {
                              notification.success({
                                message: response.message,
                              });
                              refetch();
                            });
                          }}
                        />
                      );
                    }}
                  </Form.Item>
                </div>
                {isCreate ? (
                  <ButtonOperation
                    htmlType="submit"
                    size="large"
                    comcode="save"
                    icon={<ISave color="white" />}
                    type="primary"
                    style={{ float: 'right' }}
                  />
                ) : (
                  <ButtonOperation
                    htmlType="submit"
                    size="large"
                    comcode="update"
                    icon={<ISave color="white" />}
                    type="primary"
                    style={{ float: 'right' }}
                  />
                )}
              </Row>
            }
          >
            <TabPane
              tab={
                <>
                  <ArrowLeftOutlined
                    onClick={() => {
                      navigate(`/configuration/marketingCompaign`);
                    }}
                  />
                  <GetLabel label="Marketing" />
                </>
              }
              key="1"
            >
              <Row>
                <UtinaCard
                  level={5}
                  comcode="MarketingDetail"
                  style={{ color: '#f0f' }}
                  lg={13}
                >
                  <Row gutter={18}>&nbsp;</Row>
                  <Row gutter={20}>
                    <FormInput col md={3} name="mcid">
                      <Value />
                    </FormInput>

                    <FormInput labelCol={{ span: 4 }} col md={11} name="mgid">
                      <MGListSelect allowClear />
                    </FormInput>

                    <Form.Item
                      noStyle
                      shouldUpdate={shouldUpdateCompare([
                        'mcid',
                        'mcstatuslabel',
                      ])}
                    >
                      {({ getFieldValue }) => (
                        <FormInput col md={8} name="mcstatus">
                          <Tag
                            color={
                              getFieldValue('mcstatus') === 'ACTIVE'
                                ? 'success'
                                : getFieldValue('mcstatus') === 'DISABLED'
                                ? 'error'
                                : getFieldValue('mcstatus') === 'END'
                                ? 'blue'
                                : 'default'
                            }
                          >
                            <Value value={getFieldValue('mcstatuslabel')} />
                          </Tag>
                        </FormInput>
                      )}
                    </Form.Item>

                    <Col md={12}>
                      <DF.Input
                        wrapperCol={{ span: 30 }}
                        name="mccode"
                        labelAlign="left"
                        normalize={(input: string) => input.toUpperCase()}
                        componentProps={{
                          disabled: !isCreate,
                        }}
                        rules={[{ required: true }]}
                      />
                    </Col>

                    <FormInput col md={12} name="mclabel">
                      <Input />
                    </FormInput>

                    <FormInput col md={12} name="prcode" shouldUpdate>
                      <ProductInput
                        onChange={(e) => {
                          form.setFieldsValue({
                            prcode: e,
                            offerid: '',
                            ofrid: undefined,
                            ofrtype: undefined,
                            ofrtypelabel: undefined,
                          });
                        }}
                        allowClear
                      />
                    </FormInput>

                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => (
                        <FormInput col md={12} name="offerid">
                          <OfferInput
                            params={{
                              mgid: Number(localStorage.getItem('mgid')),
                              prcode: getFieldValue('prcode'),
                            }}
                            onSelect={(e) => {
                              form.setFieldsValue({
                                offerid: e,
                                ofrid: undefined,
                                ofrtype: '',
                              });
                            }}
                          />
                        </FormInput>
                      )}
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => (
                        <FormInput
                          col
                          md={12}
                          name="ofrid"
                          shouldUpdate={shouldUpdateCompare([
                            'prcode',
                            'offerid',
                          ])}
                        >
                          <PricingInput
                            params={{
                              catalogid: 0,
                              duration: 0,
                              filters: '',
                              filtersList: getFilterList(),
                              finamount: 0,
                              mgid: Number(localStorage.getItem('mgid')),
                              offerid: form.getFieldValue('offerid'),
                              prcode: form.getFieldValue('prcode'),
                            }}
                            onChange={(e) => {
                              form.setFieldsValue({
                                ofrid: e.ofrid,
                                ofrtype: e.ofrtype,
                              });
              
                            }}
                            allowClear
                          />
                        </FormInput>
                      )}
                    </Form.Item>

                    <FormInput col md={12} name="networkcode">
                      <ParListSelect parcode="NETWORK" />
                    </FormInput>

                    <FormInput col md={12} name="manufacturer">
                      <ThirdPartyInput role="OEM" />
                    </FormInput>

                    <FormInput col md={12} name="broker">
                      <ThirdPartyInput role="BROKER" />
                    </FormInput>

                    <FormInput col md={12} name="startdate">
                      <DatePicker showToday />
                    </FormInput>

                    <FormInput col md={12} name="enddate">
                      <DatePicker />
                    </FormInput>
                  </Row>
                  <UtinaCard
                    level={5}
                    comcode="Financed Amount"
                    lg={20}
                    style={{ color: 'red', marginLeft: '90px !important' }}
                  >
                    <Row gutter={90}>
                      <FormInput
                        col
                        md={12}
                        name="currcode"
                        labelCol={{ span: '2px' }}
                      >
                        <Value />
                      </FormInput>
                      <Form.Item
                        noStyle
                        shouldUpdate={shouldUpdateCompare(['currcode'])}
                      >
                        {({ getFieldValue }) => {
                          return (
                            <FormInput
                              col
                              md={12}
                              name="assettaxamount"
                              labelCol={{ span: '2px' }}
                            >
                              <Text>
                                {toCurrency(
                                  getFieldValue('assettaxamount'),
                                  getFieldValue('currcode')
                                )}
                              </Text>
                            </FormInput>
                          );
                        }}
                      </Form.Item>
                      <Form.Item
                        noStyle
                        shouldUpdate={shouldUpdateCompare(['currcode'])}
                      >
                        {({ getFieldValue }) => {
                          return (
                            <FormInput
                              col
                              md={12}
                              name="assetnetamount"
                              labelCol={{ span: '2px' }}
                            >
                              <Text>
                                {toCurrency(
                                  getFieldValue('assetnetamount'),
                                  getFieldValue('currcode')
                                )}
                              </Text>
                            </FormInput>
                          );
                        }}
                      </Form.Item>
                      <Form.Item
                        noStyle
                        shouldUpdate={shouldUpdateCompare(['currcode'])}
                      >
                        {({ getFieldValue }) => {
                          return (
                            <FormInput
                              col
                              md={12}
                              name="assetgrossamount"
                              labelCol={{ span: '2px' }}
                            >
                              <Text>
                                {toCurrency(
                                  getFieldValue('assetgrossamount'),
                                  getFieldValue('currcode')
                                )}
                              </Text>
                            </FormInput>
                          );
                        }}
                      </Form.Item>
                    </Row>
                  </UtinaCard>
                </UtinaCard>

                <UtinaCard
                  level={5}
                  comcode="catalog"
                  style={{ color: '#000', marginLeft: 30 }}
                  lg={10}
                >
                  <Row gutter={18}>&nbsp;</Row>
                  <Row justify="center">
                    <Col span={15}>
                      <ModalController>
                        {({ setVisible, visible }) => (
                          <>
                            <Button
                              type="default"
                              icon={<AppstoreAddOutlined />}
                              onClick={() => setVisible(true)}
                              style={{
                                width: '100%',
                                height: 40,
                                marginBottom: 1,
                              }}
                            >
                              <GetLabel label="Upload Marketing Default Pictures" />
                            </Button>
                            <MarketingPicturesModal
                              onCancel={() => {
                                refetch();
                                setVisible(false);
                              }}
                              onSuccess={() => {
                                refetch();
                                setVisible(false);
                              }}
                              visible={visible}
                              form={form}
                              ParMarketingResponse={mc}
                              onValidatePicture={(url) => {
                                setCatalog({ ...catalog, pictureurl: url });
                              }}
                            />
                          </>
                        )}
                      </ModalController>
                    </Col>
                  </Row>
                  <Row justify="center">
                    <Col span={15}>
                      <Image
                        width="100%"
                        height="350px"
                        src={CATALOG_IMG_URL + catalog?.pictureurl}
                        fallback="data:image/png;base64,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"
                      />
                    </Col>
                  </Row>
                </UtinaCard>
              </Row>
            </TabPane>

            <TabPane
              tab={<GetLabel label="Label" />}
              disabled={isCreate}
              key="2"
            >
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => (
                  <MarketingLabelList
                    isFetching={isFetching}
                    refetch={refetch}
                    data={getFieldValue('parMarketingLabels')}
                    mc={marketingComp ? marketingComp : INITIAL_VALUES}
                  />
                )}
              </Form.Item>
            </TabPane>

            <TabPane
              tab={<GetLabel label="Asset" />}
              disabled={isCreate}
              key="3"
            >
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => (
                  <ParMarketingAsset
                    data={getFieldValue('parmarketingAsset')}
                    marketingResponse={
                      marketingComp ? marketingComp : INITIAL_VALUES
                    }
                    isFetching={isFetching}
                    refetch={refetch}
                    mcid={marketingComp?.mcid ? marketingComp.mcid : 0}
                  />
                )}
              </Form.Item>
            </TabPane>
          </Tabs>
        </div>
      </Form>
    </DF.Provider>
  );
};

const INITIAL_VALUES: ParMarketingResponse = {
  assetgrossamount: 0,
  assetnetamount: 0,
  assettaxamount: 0,
  currcode: '',
  enddate: '',
  mccode: '',
  mcid: 0,
  mclabel: '',
  mcstatus: 'INIT',
  mcstatuslabel: 'Initial',
  mgid: '' as any,
  networkcode: '',
  networklabel: '',
  parMarketingLabels: Array<ParMarketingLabelResponse>(),
  picturebuttomlefturl: '',
  picturebuttomrighturl: '',
  picturecenterurl: '',
  picturemobileurl: '',
  picturetoplefturl: '',
  picturetoprighturl: '',
  pictureurl: '',
  prcode: '',
  prlabel: '',
  startdate: '',
  assets: [],
  offerid: '' as any,
  offerlabel: '',
  ofrid: '' as any,
  ofridlabel: '',
  ofrtype: '',
  ofrtypelabel: '',
  tpidmanufacturer: '' as any,
  manufacturername: '',
  manufacturerreference: '',
  tpidbroker: '' as any,
  brokername: '',
  brokerreference: '',
};

export default MarketingCompaignDetail;
